// Client-side API auth shim.
//
// When VITE_APP_ACCESS_TOKEN is configured at build time, attach it as the
// `x-app-token` header to every same-origin /api/ request. The server validates
// it in api/_lib/common.ts → guardRequest(). When the env var is absent
// (the default), this is a complete no-op and the app behaves exactly as before.
//
// Installed once at boot (src/main.ts) by wrapping window.fetch, so the ~20
// scattered `fetch('/api/...')` call sites don't each need editing — and so the
// origin-allowlist half of the guard (which needs no token) keeps working
// regardless.

// import.meta.env is statically replaced by Vite at build time. Cast through a
// local shape rather than relying on vite/client types (not referenced here) —
// matches the pattern in src/playcanvas/analytics.ts.
const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env ?? {};
const TOKEN = (env.VITE_APP_ACCESS_TOKEN ?? '').trim();

function isApiPath(url: string): boolean {
  if (url.startsWith('/api/')) return true;
  try {
    const u = new URL(url, window.location.origin);
    return u.origin === window.location.origin && u.pathname.startsWith('/api/');
  } catch {
    return false;
  }
}

export function installApiAuth(): void {
  if (!TOKEN) return; // opt-in; off by default
  const native = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url =
      typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    if (!isApiPath(url)) return native(input, init);
    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    headers.set('x-app-token', TOKEN);
    return native(input, { ...init, headers });
  };
}
